import type { ReactiveController, ReactiveControllerHost } from 'lit';
import type { Todo, TodoWithMeta, TodoCategory, TodoStatus } from '../types/todo.js';
import * as todoApi from './todo-api.js';
import { getCategories } from './category-api.js';
import { wsClient } from './websocket-client.js';

export type TodoLoadingState = 'idle' | 'loading' | 'loaded' | 'error';

let sharedTodos: TodoWithMeta[] = [];
let sharedCategories: TodoCategory[] = [];
let sharedActivePseudoProjectId: number | null = null;
let sharedLoadingState: TodoLoadingState = 'idle';
let sharedError: string | null = null;

const hosts = new Set<ReactiveControllerHost>();

function notifyHosts(): void {
  for (const host of hosts) {
    host.requestUpdate();
  }
}

function enrichTodos(todos: Todo[], categories: TodoCategory[]): TodoWithMeta[] {
  const categoryMap = new Map(categories.map(c => [c.id, c]));
  return todos.map(todo => {
    const cat = todo.category_id ? categoryMap.get(todo.category_id) : null;
    const raw = todo as unknown as Record<string, unknown>;
    return {
      ...todo,
      sub_ticket_total: (typeof raw.sub_ticket_total === 'number' ? raw.sub_ticket_total : 0),
      sub_ticket_done: (typeof raw.sub_ticket_done === 'number' ? raw.sub_ticket_done : 0),
      category_color: cat?.color ?? null,
      category_name: cat?.name ?? null,
    };
  });
}

async function loadTodos(pseudoProjectId: number): Promise<void> {
  if (sharedLoadingState === 'loading') return;
  sharedLoadingState = 'loading';
  sharedError = null;
  sharedActivePseudoProjectId = pseudoProjectId;
  notifyHosts();

  try {
    const [todos, categories] = await Promise.all([
      todoApi.getTodos(pseudoProjectId),
      getCategories(),
    ]);
    sharedCategories = categories;
    sharedTodos = enrichTodos(todos, categories);
    sharedLoadingState = 'loaded';
  } catch (e) {
    sharedLoadingState = 'error';
    sharedError = e instanceof Error ? e.message : 'To-Dos konnten nicht geladen werden';
  }

  notifyHosts();
}

function onWsMessage(e: Event): void {
  const detail = (e as CustomEvent).detail as { type: string; payload: Todo };
  const type = detail.type;

  if (type === 'todo:created' || type === 'todo:updated' || type === 'todo:status-changed' || type === 'todo:deleted') {
    if (sharedActivePseudoProjectId !== null) {
      void loadTodos(sharedActivePseudoProjectId);
    }
  }
}

wsClient.addEventListener('ws-message', onWsMessage);

export class TodoStateController implements ReactiveController {
  private host: ReactiveControllerHost;

  constructor(host: ReactiveControllerHost) {
    this.host = host;
    host.addController(this);
  }

  hostConnected(): void {
    hosts.add(this.host);
  }

  hostDisconnected(): void {
    hosts.delete(this.host);
  }

  get todos(): TodoWithMeta[] {
    return sharedTodos;
  }

  get categories(): TodoCategory[] {
    return sharedCategories;
  }

  get loadingState(): TodoLoadingState {
    return sharedLoadingState;
  }

  get error(): string | null {
    return sharedError;
  }

  get activePseudoProjectId(): number | null {
    return sharedActivePseudoProjectId;
  }

  todosByStatus(status: TodoStatus): TodoWithMeta[] {
    return sharedTodos.filter(t => t.status === status);
  }

  async loadForProject(pseudoProjectId: number): Promise<void> {
    await loadTodos(pseudoProjectId);
  }

  async updateStatus(todoId: number, status: TodoStatus): Promise<void> {
    try {
      await todoApi.updateTodoStatus(todoId, status);
      if (sharedActivePseudoProjectId !== null) {
        await loadTodos(sharedActivePseudoProjectId);
      }
    } catch (e) {
      sharedError = e instanceof Error ? e.message : 'Status-Update fehlgeschlagen';
      notifyHosts();
    }
  }

  async refresh(): Promise<void> {
    if (sharedActivePseudoProjectId !== null) {
      await loadTodos(sharedActivePseudoProjectId);
    }
  }
}
